import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import { OrganizationService } from '../infrastructure/organization.service';
import type { OrgSettingsDTO, UpdateOrgSettingsInput } from '../infrastructure/organization.service';
import { useOrganizationThemeStore } from '../infrastructure/organization-theme.store';

const organizationService = new OrganizationService();

export const organizationKeys = {
  all: ['organization'] as const,
  settings: () => [...organizationKeys.all, 'settings'] as const,
};

function syncThemeFromOrgSettings(data: OrgSettingsDTO): void {
  const theme = useOrganizationThemeStore.getState();
  useOrganizationThemeStore.getState().setForOrganization(data.id, {
    primaryColor: theme.colors.primary,
    accentColor: theme.colors.accent,
  });
}

export function useOrganizationSettings(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: organizationKeys.settings(),
    queryFn: () => organizationService.getSettings(),
    staleTime: QUERY_STALE_MS.CATALOG_LONG,
    gcTime: QUERY_STALE_MS.REFERENCE,
    enabled: options?.enabled ?? true,
  });

  useEffect(() => {
    if (query.data) syncThemeFromOrgSettings(query.data);
  }, [query.data]);

  return query;
}

export function useUpdateOrganizationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOrgSettingsInput) => organizationService.updateSettings(input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: organizationKeys.settings() });
      // Keep brand-logo resolution cache in sync (see brandLogoKeys in settings).
      qc.invalidateQueries({ queryKey: ['branding', 'logo'] });
      syncThemeFromOrgSettings(data);
    },
  });
}
